import { useIonViewWillEnter } from '@ionic/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LocationsApi, Permission } from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@up2stay/up2promo-ui';
import { AlertCircle, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  ListEmptyState,
  ListPageBody,
  ListSearchField,
  ListToolbarSurface,
  listToolbarFilterChipClass,
} from '@/core/components/layout';
import {
  getLocationHealthSummary,
  locationAttentionRank,
  locationNeedsAttention,
  type LocationReviewStats,
} from '@/core/components/locations/location-health';
import type { LocationHealthLabels } from '@/core/components/locations/location-health-indicators';
import {
  type LocationReviewLabels,
  type LocationRow,
  LocationsCardGrid,
} from '@/core/components/locations/locations-card-grid';
import {
  parseLocationsAttentionFromSearch,
  replaceLocationsAttentionSearch,
} from '@/core/components/locations/locations-list-filter-url';
import { PageHeader } from '@/core/components/page-header';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  useChannelLocations,
  useReputationAnalytics,
} from '@/core/hooks/use-reputation';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';
import { AppIonPage } from '@/core/components/app-ion-page';

function LocationsBreadcrumb({
  domainLabel,
  locationsLabel,
}: {
  domainLabel: string;
  locationsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {locationsLabel}
    </>
  );
}

const LOCATIONS_PAGE_SIZE = 10;
/** Workspace board sample — enough for SMB roll-up + needs-attention filter. */
const BOARD_HEALTH_LIMIT = 200;

type LocationsDict = {
  title?: string;
  description?: string;
  search?: string;
  clearSearch?: string;
  createLocation?: string;
  addCard?: string;
  selectWorkspace?: string;
  empty?: {
    title?: string;
    description?: string;
    descriptionReadOnly?: string;
  };
  noResults?: { title?: string; description?: string };
  health?: {
    googleLinked?: string;
    googleListing?: string;
    googleNeedsAction?: string;
    googleOptOut?: string;
    googleSyncFailed?: string;
    linksOk?: string;
    linksEmpty?: string;
    googleChannel?: string;
    linksChannel?: string;
    ariaGroup?: string;
  };
  summaryCount?: string;
  summaryCountOne?: string;
  summaryNeedsAttention?: string;
  summaryNeedsAttentionOne?: string;
  filterNeedsAttention?: string;
  filterNeedsAttentionAria?: string;
  filterNeedsAttentionEmpty?: string;
  filterNeedsAttentionEmptyDescription?: string;
  filterShowAll?: string;
  loadError?: {
    title?: string;
    description?: string;
    retry?: string;
  };
  cardReviewSummary?: string;
  cardReviewSummaryAria?: string;
};

function sortBrokenFirst(
  rows: LocationRow[],
  healthById: Record<string, ReturnType<typeof getLocationHealthSummary>>,
  includeReputation: boolean,
): LocationRow[] {
  return [...rows].sort((a, b) => {
    return (
      locationAttentionRank(healthById[a.id], { includeReputation }) -
      locationAttentionRank(healthById[b.id], { includeReputation })
    );
  });
}

export function LocationsPage() {
  const dict = useDictionary();
  const locale = useLocale();
  const { navigateShell } = useNavigateShell();
  const routeLocation = useLocation();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const { isFeatureEnabled } = useEntitlements();
  const reputationEnabled = isFeatureEnabled('feature.reputation');
  const canCreate = can(Permission.LOCATIONS_MANAGE);

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(() =>
    parseLocationsAttentionFromSearch(routeLocation.search),
  );
  const [appliedAttentionSearch, setAppliedAttentionSearch] = useState(
    routeLocation.search,
  );
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (routeLocation.search !== appliedAttentionSearch) {
    setAppliedAttentionSearch(routeLocation.search);
    const fromUrl = parseLocationsAttentionFromSearch(routeLocation.search);
    if (fromUrl !== needsAttentionOnly) {
      setNeedsAttentionOnly(fromUrl);
    }
  }

  useIonViewWillEnter(() => {
    void queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
    void queryClient.invalidateQueries({
      queryKey: reputationQueryKeys.channelLocationsRoot,
    });
    void queryClient.invalidateQueries({
      queryKey: reputationQueryKeys.analyticsRoot,
    });
  });

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearchQuery.trim() && page !== 1) {
      queueMicrotask(() => setPage(1));
    }
  }, [debouncedSearchQuery, page]);

  useEffect(() => {
    if (needsAttentionOnly && page !== 1) {
      queueMicrotask(() => setPage(1));
    }
  }, [needsAttentionOnly, page]);

  /** Full-ish workspace sample for Status Board roll-up + attention filter. */
  const {
    locations: boardLocations,
    isPending: boardPending,
    isError: boardError,
    refetch: refetchBoard,
  } = useLocations(currentWorkspace?.id, {
    limit: BOARD_HEALTH_LIMIT,
    queryKeySuffix: 'board-health',
    staleTime: 30 * 1000,
    enabled: !!currentWorkspace?.id,
  });

  /** No locations at all: the attention filter has nothing to judge (DEV-548). */
  const workspaceHasNoLocations =
    !boardPending && !boardError && boardLocations.length === 0;
  const attentionFilterOn = needsAttentionOnly && !workspaceHasNoLocations;

  const queryKey = [
    'locations',
    currentWorkspace?.id,
    debouncedSearchQuery,
    page,
  ] as const;

  const {
    data,
    isLoading,
    isError,
    refetch: refetchPage,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!currentWorkspace) {
        return {
          data: [],
          totalPages: 1,
          hasNextPage: false,
          totalCount: 0,
        };
      }
      const response = await LocationsApi.findAll({
        path: { workspaceId: currentWorkspace.id },
        query: {
          page,
          limit: LOCATIONS_PAGE_SIZE,
          ...(debouncedSearchQuery.trim()
            ? {
                filters: JSON.stringify({
                  search: debouncedSearchQuery.trim(),
                }),
              }
            : {}),
        },
      });
      return {
        data: response.data?.data ?? [],
        totalPages: response.data?.totalPages ?? 1,
        hasNextPage: response.data?.hasNextPage ?? false,
        totalCount:
          response.data?.totalCount ?? response.data?.data?.length ?? 0,
      };
    },
    enabled: !!currentWorkspace?.id && !attentionFilterOn,
    staleTime: 30 * 1000,
  });

  const { data: channelLocations = [] } = useChannelLocations(
    reputationEnabled ? currentWorkspace?.id : undefined,
  );

  /** Workspace-scoped location breakdown — aggregated across synced providers. */
  const { data: reputationAnalytics } = useReputationAnalytics(
    reputationEnabled ? currentWorkspace?.id : undefined,
  );

  const pageLocations = useMemo(() => data?.data ?? [], [data?.data]);
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;

  const boardHealthById = useMemo(() => {
    const map: Record<string, ReturnType<typeof getLocationHealthSummary>> = {};
    for (const location of boardLocations) {
      map[location.id] = getLocationHealthSummary(location, channelLocations);
    }
    return map;
  }, [boardLocations, channelLocations]);

  const pageHealthById = useMemo(() => {
    const map: Record<string, ReturnType<typeof getLocationHealthSummary>> = {};
    for (const location of pageLocations) {
      map[location.id] =
        boardHealthById[location.id] ??
        getLocationHealthSummary(location, channelLocations);
    }
    return map;
  }, [pageLocations, boardHealthById, channelLocations]);

  const reviewStatsById = useMemo(() => {
    const map: Record<string, LocationReviewStats> = {};
    for (const row of reputationAnalytics?.locations ?? []) {
      if (row.totalReviews > 0) {
        map[row.locationId] = {
          averageRating: row.averageRating,
          totalReviews: row.totalReviews,
        };
      }
    }
    return map;
  }, [reputationAnalytics?.locations]);

  const dictLocations = (dict as { locations?: LocationsDict }).locations;
  const healthDict = dictLocations?.health;

  const healthLabels: LocationHealthLabels = {
    googleLinked: healthDict?.googleLinked ?? 'Synced',
    googleListing: healthDict?.googleListing ?? 'Listing only',
    googleNeedsAction: healthDict?.googleNeedsAction ?? 'Needs action',
    googleOptOut: healthDict?.googleOptOut ?? 'Not used',
    googleSyncFailed: healthDict?.googleSyncFailed ?? 'Sync failed',
    linksOk: healthDict?.linksOk ?? 'Set',
    linksEmpty: healthDict?.linksEmpty ?? 'None',
    googleChannel: healthDict?.googleChannel ?? 'Reviews',
    linksChannel: healthDict?.linksChannel ?? 'Links',
    ariaGroup: healthDict?.ariaGroup ?? 'Location health',
  };

  const reviewLabels: LocationReviewLabels = {
    summary: dictLocations?.cardReviewSummary ?? '★ {rating} · {count} reviews',
    summaryAria:
      dictLocations?.cardReviewSummaryAria ??
      '{rating} average from {count} reviews',
  };

  const needsAttentionCount = useMemo(() => {
    const scope = { includeReputation: reputationEnabled };
    let count = 0;
    for (const location of boardLocations) {
      const health = boardHealthById[location.id];
      if (health && locationNeedsAttention(health, scope)) {
        count += 1;
      }
    }
    return count;
  }, [boardLocations, boardHealthById, reputationEnabled]);

  const attentionLocations = useMemo(() => {
    const scope = { includeReputation: reputationEnabled };
    const needle = debouncedSearchQuery.trim().toLowerCase();
    const rows = boardLocations.filter((location) => {
      const health = boardHealthById[location.id];
      if (!health || !locationNeedsAttention(health, scope)) return false;
      if (!needle) return true;
      const haystack =
        `${location.name} ${location.address ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
    return sortBrokenFirst(rows, boardHealthById, reputationEnabled);
  }, [
    boardLocations,
    boardHealthById,
    debouncedSearchQuery,
    reputationEnabled,
  ]);

  const displayLocations = useMemo(() => {
    if (attentionFilterOn) {
      return attentionLocations;
    }
    return sortBrokenFirst(pageLocations, pageHealthById, reputationEnabled);
  }, [
    attentionFilterOn,
    attentionLocations,
    pageLocations,
    pageHealthById,
    reputationEnabled,
  ]);

  const displayHealthById = attentionFilterOn
    ? boardHealthById
    : pageHealthById;

  /** Workspace roll-up — always from board sample, not the current page slice. */
  const statusSummary = useMemo(() => {
    if (boardPending && boardLocations.length === 0) {
      return null;
    }
    const boardCount = boardLocations.length;
    if (boardCount === 0 && totalCount === 0) {
      return null;
    }
    const countForSummary = attentionFilterOn
      ? attentionLocations.length
      : totalCount > 0
        ? totalCount
        : boardCount;
    if (countForSummary === 0) {
      return null;
    }
    const countTemplate =
      countForSummary === 1
        ? (dictLocations?.summaryCountOne ??
          dictLocations?.summaryCount ??
          '{count} location')
        : (dictLocations?.summaryCount ?? '{count} locations');
    const countPart = countTemplate.replace('{count}', String(countForSummary));
    if (needsAttentionCount === 0) {
      return countPart;
    }
    const attentionTemplate =
      needsAttentionCount === 1
        ? (dictLocations?.summaryNeedsAttentionOne ??
          dictLocations?.summaryNeedsAttention ??
          '{count} needs attention')
        : (dictLocations?.summaryNeedsAttention ?? '{count} need attention');
    const attentionPart = attentionTemplate.replace(
      '{count}',
      String(needsAttentionCount),
    );
    return `${countPart} · ${attentionPart}`;
  }, [
    boardPending,
    boardLocations.length,
    attentionLocations.length,
    totalCount,
    attentionFilterOn,
    needsAttentionCount,
    dictLocations?.summaryCount,
    dictLocations?.summaryCountOne,
    dictLocations?.summaryNeedsAttention,
    dictLocations?.summaryNeedsAttentionOne,
  ]);

  const breadcrumb = (
    <LocationsBreadcrumb
      domainLabel={dict.nav.navDomainWorkspace}
      locationsLabel={dict.nav.locations}
    />
  );

  if (!currentWorkspace) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader
              title={dictLocations?.title ?? 'Locations'}
              breadcrumb={breadcrumb}
            />
            {dictLocations?.selectWorkspace ? (
              <p className="px-1 text-[12px] leading-snug text-muted-foreground/85">
                {dictLocations.selectWorkspace}
              </p>
            ) : null}
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  const title = dictLocations?.title ?? 'Locations';
  const searchPlaceholder = dictLocations?.search ?? 'Search…';
  const clearSearchLabel = dictLocations?.clearSearch ?? 'Clear search';
  const createLocationLabel = dictLocations?.createLocation ?? 'Add location';
  const addCardLabel = dictLocations?.addCard ?? 'Add location';
  const emptyTitle = dictLocations?.empty?.title ?? 'No locations yet';
  const emptyDescription = canCreate
    ? dictLocations?.empty?.description
    : dictLocations?.empty?.descriptionReadOnly;
  const noResultsTitle =
    dictLocations?.noResults?.title ?? 'No locations found';
  const noResultsDescription = dictLocations?.noResults?.description;
  const filterLabel = dictLocations?.filterNeedsAttention ?? 'Needs attention';
  const filterAria =
    dictLocations?.filterNeedsAttentionAria ??
    'Show only locations that need attention';
  const filterEmptyTitle =
    dictLocations?.filterNeedsAttentionEmpty ?? 'Nothing needs attention';
  const filterEmptyDescription =
    dictLocations?.filterNeedsAttentionEmptyDescription ??
    'All locations in this board look healthy.';
  const filterShowAllLabel =
    dictLocations?.filterShowAll ?? 'Show all locations';
  const loadErrorTitle =
    dictLocations?.loadError?.title ?? 'Could not load locations';
  const loadErrorDescription =
    dictLocations?.loadError?.description ??
    'Something went wrong while loading your locations. Please try again.';
  const loadErrorRetry = dictLocations?.loadError?.retry ?? 'Try again';

  const goToLocation = (location: LocationRow) => {
    navigateShell(shellPaths.moreWorkspaceLocationDetail(location.id));
  };

  const goToCreate = () => {
    navigateShell(shellPaths.moreWorkspaceLocationCreate);
  };

  const applyAttentionFilter = (enabled: boolean) => {
    setNeedsAttentionOnly(enabled);
    const nextSearch = replaceLocationsAttentionSearch(
      routeLocation.search,
      enabled,
    );
    setAppliedAttentionSearch(nextSearch);
    navigateShell(`${routeLocation.pathname}${nextSearch}`, {
      intent: 'updateQuery',
    });
  };

  const retryLoad = () => {
    if (attentionFilterOn || boardError) {
      void refetchBoard();
    }
    if (!attentionFilterOn) {
      void refetchPage();
    }
  };

  const showLoadError =
    (attentionFilterOn && boardError) || (!attentionFilterOn && isError);

  const listLoading = attentionFilterOn
    ? boardPending
    : isLoading || (boardPending && boardLocations.length === 0);

  const showPagination = !attentionFilterOn && totalPages > 1 && !listLoading;

  const filterEmptyAction =
    attentionFilterOn &&
    !debouncedSearchQuery.trim() &&
    displayLocations.length === 0 &&
    !listLoading &&
    !showLoadError ? (
      <Button
        type="button"
        variant="outline"
        onClick={() => applyAttentionFilter(false)}
      >
        {filterShowAllLabel}
      </Button>
    ) : undefined;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <PageHeader
            title={title}
            breadcrumb={breadcrumb}
            actionsLayout="stack"
            actions={
              canCreate ? (
                <Button
                  onClick={goToCreate}
                  size="md"
                  className="w-full sm:w-auto"
                  data-guidance-target="locations-empty"
                >
                  <Plus className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">{createLocationLabel}</span>
                </Button>
              ) : undefined
            }
          />
          <ListPageBody>
            <ListToolbarSurface>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                <ListSearchField
                  className="min-w-0 w-full sm:flex-1"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => setSearchQuery('')}
                  clearAriaLabel={clearSearchLabel}
                />
                <button
                  type="button"
                  aria-pressed={attentionFilterOn}
                  aria-label={filterAria}
                  disabled={workspaceHasNoLocations}
                  onClick={() => applyAttentionFilter(!attentionFilterOn)}
                  className={cn(
                    listToolbarFilterChipClass,
                    'h-10 min-w-0 shrink-0 justify-center gap-1.5 self-stretch px-3 disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto',
                    attentionFilterOn
                      ? 'border-destructive/40 bg-destructive/10 text-destructive'
                      : 'border-border/60 bg-card text-foreground hover:bg-muted/40',
                  )}
                >
                  <span className="truncate">{filterLabel}</span>
                  {needsAttentionCount > 0 ? (
                    <span className="tabular-nums opacity-80">
                      {needsAttentionCount}
                    </span>
                  ) : null}
                </button>
              </div>
            </ListToolbarSurface>

            {statusSummary && !showLoadError ? (
              <p
                className="text-[13.5px] leading-relaxed text-muted-foreground"
                aria-live="polite"
              >
                {statusSummary}
              </p>
            ) : null}

            {showLoadError ? (
              <ListEmptyState
                tone="error"
                icon={<AlertCircle className="h-6 w-6" aria-hidden />}
                title={loadErrorTitle}
                description={loadErrorDescription}
                action={
                  <Button variant="outline" onClick={retryLoad}>
                    {loadErrorRetry}
                  </Button>
                }
              />
            ) : (
              <LocationsCardGrid
                locations={displayLocations}
                healthById={displayHealthById}
                healthLabels={healthLabels}
                reviewStatsById={reviewStatsById}
                reviewLabels={reviewLabels}
                locale={locale}
                showReviewSlot={reputationEnabled}
                includeReputation={reputationEnabled}
                isLoading={listLoading}
                searchQuery={
                  attentionFilterOn &&
                  !debouncedSearchQuery.trim() &&
                  displayLocations.length === 0 &&
                  needsAttentionCount === 0
                    ? undefined
                    : debouncedSearchQuery
                }
                emptyTitle={
                  attentionFilterOn && !debouncedSearchQuery.trim()
                    ? filterEmptyTitle
                    : emptyTitle
                }
                emptyDescription={
                  attentionFilterOn && !debouncedSearchQuery.trim()
                    ? filterEmptyDescription
                    : emptyDescription
                }
                emptyAction={filterEmptyAction}
                noResultsTitle={noResultsTitle}
                noResultsDescription={noResultsDescription}
                createLabel={createLocationLabel}
                addCardLabel={addCardLabel}
                canCreate={canCreate && !attentionFilterOn}
                onCreate={goToCreate}
                onLocationClick={goToLocation}
              />
            )}

            {showPagination && !showLoadError ? (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      size="md"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1) {
                          setPage(page - 1);
                        }
                      }}
                      className={
                        page <= 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              size="icon"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    },
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      size="md"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < totalPages) {
                          setPage(page + 1);
                        }
                      }}
                      className={
                        page >= totalPages
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            ) : null}
          </ListPageBody>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
